/**
 * S08 · Quick Note — real data edition. Port of the showcase note editor
 * with controlled inputs and the mutation that writes to SQLite. The
 * fake on-screen keyboard is gone: on phone the OS keyboard handles it.
 * This route is marked `fullscreen: true` so AppShell hides its chrome
 * while the user writes.
 *
 * The attachment row hands off to the capture routes, saving the note first
 * so nothing typed is lost on the way — see `saveThenCapture` and
 * `ATTACHMENTS`.
 */
import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { GestureBar, I, Ico, ScreenFrame, StatusBar, T } from '@/components/primitives';
import { useCreateNoteMutation, useSession } from '@kapture/client-core';
import { SubjectChip } from '@/components/SubjectChip';

export const Route = createFileRoute('/sessions/$sessionId/capture/note')({
  component: QuickNotePage,
  staticData: { mobile: true, fullscreen: true },
});

/**
 * What this note can reach for, and nothing it cannot.
 *
 * The row used to be five dimmed glyphs — image, mic, pin, link, list —
 * promising phase 2. Three of them are captures this app already makes, so
 * they are launchers now. `link` and `list` were rich-text editor features
 * with no model anywhere behind them: no capture kind, no field, no column.
 * Two more icons deferring to a phase nobody had scoped is worse than four
 * that work, so they are gone rather than deferred again.
 */
const ATTACHMENTS = [
  { icon: I.image, label: 'Add a photo', to: '/sessions/$sessionId/capture/photo' },
  { icon: I.mic, label: 'Record a voice note', to: '/sessions/$sessionId/capture/voice' },
  { icon: I.pin, label: 'Drop a location pin', to: '/sessions/$sessionId/capture/gps' },
] as const;

function QuickNotePage() {
  const { sessionId } = Route.useParams();
  const navigate = useNavigate();
  const { data: session } = useSession(sessionId);
  const createNote = useCreateNoteMutation();
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');

  const canSave =
    (title.trim().length > 0 || body.trim().length > 0) &&
    !createNote.isPending &&
    Boolean(sessionId);

  async function save() {
    if (!canSave) return;
    await createNote.mutateAsync({
      sessionId,
      title: title.trim(),
      body: body.trim(),
    });
    navigate({ to: '/sessions/$sessionId', params: { sessionId } });
  }

  function close() {
    navigate({ to: '/sessions/$sessionId', params: { sessionId } });
  }

  /**
   * Leaving for a capture must not cost what has been typed.
   *
   * The note screen holds its text in component state, so navigating away
   * discards it. Committing first means the inspector comes back to a session
   * carrying both the note and whatever they just captured, in the order they
   * made them. An untouched note is not worth a row, so an empty one just
   * navigates.
   */
  async function saveThenCapture(to: (typeof ATTACHMENTS)[number]['to']) {
    if (canSave) {
      await createNote.mutateAsync({
        sessionId,
        title: title.trim(),
        body: body.trim(),
      });
    }
    navigate({ to, params: { sessionId } });
  }

  return (
    <ScreenFrame tone="light">
      <StatusBar />
      <div
        style={{
          padding: '8px 16px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: `1px solid ${T.stroke}`,
        }}
      >
        <button
          type="button"
          onClick={close}
          aria-label="Cancel note"
          style={{
            background: 'transparent',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
            color: T.text,
            display: 'flex',
          }}
        >
          <Ico d={I.close} size={20} />
        </button>
        <div
          style={{
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 4,
          }}
        >
          <div style={{ fontSize: 14, fontWeight: 700, fontFamily: T.font }}>New note</div>
          <SubjectChip tone="light" />
          {session?.title && (
            <div
              style={{
                fontSize: 10,
                color: T.textMute,
                fontFamily: T.mono,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                marginTop: 1,
                maxWidth: 160,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {session.title}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={() => void save()}
          disabled={!canSave}
          aria-label="Save note"
          style={{
            background: 'transparent',
            border: 'none',
            padding: 0,
            cursor: canSave ? 'pointer' : 'not-allowed',
            color: canSave ? T.brand : T.textMute,
            fontSize: 13,
            fontWeight: 600,
            fontFamily: T.font,
          }}
        >
          {createNote.isPending ? 'Saving…' : 'Save'}
        </button>
      </div>

      <div
        style={{
          padding: 16,
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          overflow: 'auto',
        }}
      >
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Title (optional)"
          autoFocus
          style={{
            fontSize: 20,
            fontWeight: 700,
            fontFamily: T.font,
            letterSpacing: '-0.02em',
            border: 'none',
            outline: 'none',
            padding: 0,
            color: T.text,
            background: 'transparent',
          }}
        />
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Jot your observation — boat access needed, harbormaster slot Tuesday, tide 10:30-12:15…"
          style={{
            flex: 1,
            minHeight: 200,
            fontSize: 15,
            fontFamily: T.font,
            lineHeight: 1.55,
            color: T.text,
            border: 'none',
            outline: 'none',
            resize: 'none',
            padding: 0,
            background: 'transparent',
          }}
        />
        <div
          style={{
            display: 'flex',
            gap: 8,
            padding: '8px 0',
            borderTop: `1px solid ${T.stroke}`,
            marginTop: 'auto',
            alignItems: 'center',
          }}
        >
          {ATTACHMENTS.map((a) => (
            <button
              key={a.to}
              type="button"
              onClick={() => void saveThenCapture(a.to)}
              aria-label={a.label}
              title={a.label}
              disabled={createNote.isPending}
              style={{
                width: 36,
                height: 36,
                borderRadius: 8,
                border: 'none',
                background: T.surface2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: T.textDim,
                cursor: 'pointer',
              }}
            >
              <Ico d={a.icon} size={16} />
            </button>
          ))}
        </div>
      </div>

      {createNote.isError && (
        <div
          style={{
            margin: '0 16px 8px',
            padding: '8px 12px',
            background: T.dangerSoft,
            border: `1px solid ${T.dangerSoft}`,
            borderRadius: 10,
            color: T.dangerText,
            fontSize: 12,
            fontFamily: T.font,
          }}
        >
          Couldn't save note. Try again.
        </div>
      )}
      <GestureBar />
    </ScreenFrame>
  );
}
